import usePersistedState from '@/hooks/usePersistedState'
import { useQueryMe } from '@/services/auth/queries'
import { OrgAccount, OrgBusinessAccount, OrgType } from '@/types/org'
import { User } from '@/types/user'
import { invalidateBrowserAuthState } from '@/utils/authState'
import { findActiveBusinessAccount, resolveBusinessId } from '@/utils/business'
import { Dispatch, ReactNode, SetStateAction, createContext, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

/* eslint-disable react-refresh/only-export-components -- context module */

export type AuthContextDefaultTypes = {
    user: User | undefined
    setUser: Dispatch<SetStateAction<User | undefined>>
    loggedIn: boolean
    setLoggedIn: Dispatch<SetStateAction<boolean>>
    orgAccount: OrgAccount | undefined
    setOrgAccount: Dispatch<SetStateAction<OrgAccount | undefined>>
    org: OrgType | undefined
    setOrg: Dispatch<SetStateAction<OrgType | undefined>>
    accounts: OrgAccount[] | undefined
    setAccounts: Dispatch<SetStateAction<OrgAccount[] | undefined>>
    clearLoggedinFromLS: () => void
    clearUserFromLS: () => void
    clearOrgAccountFromLS: () => void
    isLoading: boolean
    businessAccounts: OrgBusinessAccount[] | undefined
    setBusinessAccounts: Dispatch<SetStateAction<OrgBusinessAccount[] | undefined>>
    activeBusiness: string | undefined
    setActiveBusiness: Dispatch<SetStateAction<string | undefined>>
    activeBusinessAccount: OrgBusinessAccount | undefined
    isChangingAccount: boolean
    /** True when session is guest (e.g. magic link), no full user account yet */
    isGuest: boolean
}

const defaultValues: AuthContextDefaultTypes = {
    user: undefined,
    loggedIn: false,
    setLoggedIn: () => undefined,
    setUser: () => undefined,
    setOrgAccount: () => undefined,
    org: undefined,
    setOrg: () => undefined,
    clearLoggedinFromLS: () => undefined,
    clearUserFromLS: () => undefined,
    clearOrgAccountFromLS: () => undefined,
    orgAccount: undefined,
    accounts: undefined,
    setAccounts: () => undefined,
    isLoading: false,
    businessAccounts: undefined,
    setBusinessAccounts: () => undefined,
    activeBusiness: undefined,
    setActiveBusiness: () => undefined,
    activeBusinessAccount: undefined,
    isChangingAccount: false,
    isGuest: false,
}

export const AuthContext = createContext(defaultValues)

export default function AuthContextProvider({ children }: { children: ReactNode }) {
    const [params] = useSearchParams()
    const triggerLogin = params.get('triggerLogin')
    const [loggedIn, setLoggedIn, clearLoggedinFromLS] = usePersistedState({ key: 'loggedIn', defaultValue: false })
    const [user, setUser, clearUserFromLS] = usePersistedState({ key: 'user', defaultValue: undefined })
    const [businessAccounts, setBusinessAccounts] = usePersistedState({
        key: 'businessAccounts',
        defaultValue: [],
    })
    const [activeBusiness, setActiveBusiness] = usePersistedState({
        key: 'activeBusiness',
        defaultValue: undefined,
    })
    const [orgAccount, setOrgAccount, clearOrgAccountFromLS] = usePersistedState({
        key: 'orgAccount',
        defaultValue: undefined,
    })
    const [org, setOrg] = usePersistedState({ key: 'org', defaultValue: undefined })
    const [accounts, setAccounts] = usePersistedState({ key: 'accounts', defaultValue: undefined })
    const [isChangingAccount, setIsChangingAccount] = useState(false)

    const { data, isLoading, isError, error } = useQueryMe({ enabled: !!loggedIn || !!triggerLogin })

    useEffect(() => {
        const clearAuthContext = () => {
            invalidateBrowserAuthState()
            setLoggedIn(false)
            setUser(undefined)
            setOrgAccount(undefined)
            setOrg(undefined)
            setAccounts(undefined)
            setBusinessAccounts(undefined)
            setActiveBusiness(undefined)
        }

        if (isError && (error as any)?.response?.status === 401) {
            clearAuthContext()
            setIsChangingAccount(false)
            return
        }

        if (!loggedIn && !triggerLogin && !data) {
            setIsChangingAccount(false)
            return
        }

        if (!isLoading) {
            setIsChangingAccount(true)
            const payload = (data as any)?.data !== undefined ? (data as any).data : data
            const isGuestSession = payload?.guest === true

            if (isGuestSession) {
                setLoggedIn(true)
                setUser(undefined)
                setOrgAccount(undefined)
                setOrg(undefined)
                setAccounts(undefined)
                setBusinessAccounts(undefined)
                setActiveBusiness(undefined)
            } else if (data && payload?.user) {
                setUser(payload.user)
                setAccounts(payload.accounts)
                setBusinessAccounts(payload?.business_accounts)

                // A logged-in user must always have an active business.
                // Use the session-provided value, fall back to the first business account.
                const resolvedBusiness =
                    payload.business ||
                    resolveBusinessId(payload.business_accounts?.[0]?.business)
                setActiveBusiness(resolvedBusiness)

                const currentAccountId = payload.current_account
                const currentAccount = payload.accounts?.find((acc: OrgAccount) => acc.id === currentAccountId)
                
                // Fallback to first account if current account is not found
                const accountToUse = currentAccount || payload.accounts?.[0]
                
                setOrgAccount(accountToUse)
                setOrg(accountToUse?.org as OrgType)
                setLoggedIn(true)
            } else {
                clearAuthContext()
            }
            setIsChangingAccount(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, setUser, isLoading, loggedIn, setLoggedIn, isError, error])

    return (
        <AuthContext.Provider
            value={{
                loggedIn,
                user,
                setUser,
                setLoggedIn,
                orgAccount,
                setOrgAccount,
                clearLoggedinFromLS,
                clearUserFromLS,
                clearOrgAccountFromLS,
                org,
                setOrg,
                accounts,
                setAccounts,
                isLoading,
                businessAccounts,
                setBusinessAccounts,
                activeBusiness,
                setActiveBusiness,
                activeBusinessAccount: findActiveBusinessAccount(businessAccounts, activeBusiness),
                isChangingAccount,
                isGuest: (() => {
                    const payload = (data as any)?.data !== undefined ? (data as any).data : data
                    return payload?.guest === true
                })(),
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}
